// The canonical tenant chart palette (spec 2026-07-03-ui-wave1-color-brand-design §3).
//
// BASE_PALETTE: 12 hand-designed slots, machine-validated 2026-07-03 (dataviz
// six-checks: lightness band PASS, chroma floor PASS, worst adjacent CVD ΔE 24.2
// protan — target is ≥12). The ORDER is the CVD-safety mechanism — never reorder.
//
// Documented deviations from the dataviz reference method:
//  - 12 slots, not 8: real charts render 9+ identities (Contribution media, CP
//    top-15). Justified by validator headroom; >12 values repeat via stable modulo.
//  - No categorical red: red is reserved for semantic error/warning (backlog rule).
//
// Contrast-relief policy (validator WARN on 5 slots — amber/pink/emerald/cyan/olive
// sit below 3:1 on white): identity never rides color alone here — every ≥2-series
// chart has a legend, CP bars carry leading labels, all marks have tooltips.
//
// deriveTenantPalette: brand hue in slot 1 (nearest base hue family removed to
// avoid near-duplicates), snapped into the band; red-family brands (±20° of the
// MUI error hue) keep the base palette — brand appears in chrome only.

import { BRAND_COLOR } from './tenant'
import { hexToOklch, snapIntoBand, hueDistanceDeg } from '~/lib/oklch'

export const BASE_PALETTE: readonly string[] = [
  '#2a78d6',
  '#eda100',
  '#008300',
  '#e87ba4',
  '#4a3aa7',
  '#1baf7a',
  '#eb6834',
  '#17becf',
  '#a4552a',
  '#a2a316',
  '#6a7fdb',
  '#b0508c',
]

/** Hue of MUI error.main (#d32f2f) — the reserved red zone's center. */
const RESERVED_RED_HUE = hexToOklch('#d32f2f').h
const RED_ZONE_DEG = 20

/** An achromatic/near-gray brand has no meaningful hue (OKLab a/b are float residuals);
 *  skip brand-slot derivation entirely rather than fabricate a yellow-green slot. */
const ACHROMATIC_CHROMA_FLOOR = 0.02

export function deriveTenantPalette(brandHex: string): string[] {
  const brand = hexToOklch(brandHex)
  // Checked BEFORE the red-zone check: an achromatic hue is atan2 noise (often lands
  // near red or its own arbitrary angle) — meaningless either way, so it must not be
  // allowed to reach the hue-distance math below (red-zone or nearest-family removal).
  if (brand.c < ACHROMATIC_CHROMA_FLOOR) {
    return [...BASE_PALETTE]
  }
  if (hueDistanceDeg(brand.h, RESERVED_RED_HUE) <= RED_ZONE_DEG) {
    return [...BASE_PALETTE]
  }
  // In-band colors pass through byte-identical (snapIntoBand round-trips OKLCH
  // and may drift a channel by 1/255 — only snap when actually out of band).
  const inBand = brand.l >= 0.43 && brand.l <= 0.75 && brand.c >= 0.1
  const snapped = inBand ? brandHex.toLowerCase() : snapIntoBand(brandHex)
  const snappedHue = hexToOklch(snapped).h
  let nearestIdx = 0
  let nearestDist = Infinity
  BASE_PALETTE.forEach((hex, i) => {
    const d = hueDistanceDeg(snappedHue, hexToOklch(hex).h)
    if (d < nearestDist) {
      nearestDist = d
      nearestIdx = i
    }
  })
  return [snapped, ...BASE_PALETTE.filter((_, i) => i !== nearestIdx)]
}

export const TENANT_PALETTE: string[] = deriveTenantPalette(BRAND_COLOR)

function maxHueDistantFrom(anchor: string, candidates: string[]): string {
  const ha = hexToOklch(anchor).h
  let best = candidates[0]!
  let bestD = -1
  for (const hex of candidates) {
    const d = hueDistanceDeg(ha, hexToOklch(hex).h)
    if (d > bestD) {
      bestD = d
      best = hex
    }
  }
  return best
}

/** Semantic current-vs-compare two-tone (spec §4.1): slot 1 + the maximally
 *  hue-distant slot. Deliberately from the TENANT palette, not the user palette. */
export const COMPARISON_PAIR: [string, string] = [
  TENANT_PALETTE[0]!,
  maxHueDistantFrom(TENANT_PALETTE[0]!, TENANT_PALETTE.slice(1)),
]
